import { useRef, useState } from "react";
import { Button, Divider, Input, Modal, Tooltip } from "antd";
import { DownOutlined, EditOutlined, RightOutlined } from "@ant-design/icons";
import { SidepanelStyles } from "constants/index";
import {
  useCustomReports,
  useCustomReportsDispatch
} from "../context/custom-reports-provider";
import { ContextColumnConfigType } from "../models";

const checkIfChildFieldsAreCustomizable = (col: ContextColumnConfigType) => {
  return col.fields?.every((field) => field.customizable);
};

function RenameColumnEditorAndRenderer({
  col,
  level = 0,
  onChange
}: {
  col: ContextColumnConfigType;
  level: number;
  onChange: (fieldKey: string, value: string) => void;
}) {
  const padding = (level + 1) * 32;
  const isRenamed = !(
    col.caption_en === null ||
    col.caption_en === "" ||
    col.caption_en === col.original_caption_en
  );
  const [isEditMode, setEditMode] = useState(false);
  const [expanded, setExpanded] = useState(true);

  if (!col.customizable) {
    return null;
  }

  return (
    <div
      className="flex flex-col w-full"
      data-testid={`rename-column-row-${col.field_key}`}
    >
      <div className="flex w-full items-center justify-between text-[13px] text-ciq-text-900">
        <div className="flex space-x-1">
          {col.fields?.length &&
            checkIfChildFieldsAreCustomizable(col) &&
            (expanded ? (
              <DownOutlined
                onClick={() => setExpanded(false)}
                data-testid={`rename-column-collapse-${col.field_key}`}
              />
            ) : (
              <RightOutlined
                onClick={() => setExpanded(true)}
                data-testid={`rename-column-expand-${col.field_key}`}
              />
            ))}
          <div
            className={
              col.allow_rename ? "" : "italic text-black text-opacity-50"
            }
          >
            {" "}
            {col.caption_en}
          </div>
        </div>

        {col.allow_rename && !isEditMode && (
          <Tooltip
            title="Rename"
            placement="left"
            className="flex space-x-2"
            data-testid={`rename-column-tooltip-${col.field_key}`}
          >
            {isRenamed && (
              <span className="bg-ciq-golden-purple-1 border-ciq-golden-purple-3 text-ciq-golden-purple-7 border border-solid px-2 py-px rounded">
                Edited
              </span>
            )}

            <EditOutlined
              onClick={() => setEditMode(true)}
              className="cursor-pointer text-gray-500 hover:text-black"
              data-testid={`rename-column-edit-${col.field_key}`}
            />
          </Tooltip>
        )}
      </div>

      {isRenamed && !isEditMode && (
        <div className="text-xs italic text-black text-opacity-50">
          (Original: {col.original_caption_en})
        </div>
      )}

      {isEditMode && (
        <div className="flex w-full items-center pt-2">
          <Input
            className="text-xs"
            placeholder="Enter New Name for Column"
            allowClear
            onChange={(e) => {
              onChange(col.field_key, e.target.value); // 🔑 update via field_key
            }}
            data-testid={`rename-column-input-${col.field_key}`}
          />
        </div>
      )}

      {expanded &&
      col.fields?.length &&
      checkIfChildFieldsAreCustomizable(col) ? (
        <div
          style={{ paddingLeft: padding }}
          className="flex flex-col space-y-5 pt-8"
          data-testid={`rename-column-children-${col.field_key}`}
        >
          {col.fields.map((x) => (
            <RenameColumnEditorAndRenderer
              key={x.field_key}
              col={x}
              level={level + 1}
              onChange={onChange}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function RenameColumnsModal({
  isOpen,
  setIsOpen,
  onUpdateColumnConfig
}: {
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
  onUpdateColumnConfig: (config: ContextColumnConfigType[]) => void;
}) {
  const dispatch = useCustomReportsDispatch() as any;
  const { columnConfig } = useCustomReports();

  const columnConfigRef = useRef(structuredClone(columnConfig));

  const setContextColumnConfig = (config: ContextColumnConfigType[]) => {
    dispatch({ type: "UPDATE_COLUMN_CONFIG", payload: config });
    onUpdateColumnConfig(config);
  };

  // recursive update by field_key
  const handleChange = (fieldKey: string, value: string) => {
    const updateCaption = (
      cols: ContextColumnConfigType[]
    ): ContextColumnConfigType[] =>
      cols.map((c) =>
        c.field_key === fieldKey
          ? { ...c, caption_en: value }
          : {
              ...c,
              fields: c.fields ? updateCaption(c.fields) : c.fields
            }
      );

    columnConfigRef.current = updateCaption(columnConfigRef.current!);
  };

  return (
    <Modal
      className="custom-drawer"
      title="Rename Column Headers"
      width={420}
      style={SidepanelStyles.style}
      maskClosable
      footer={
        <div className="flex space-x-2" data-testid="rename-columns-footer">
          <Button
            onClick={() => setIsOpen(false)}
            data-testid="rename-columns-cancel"
          >
            Cancel
          </Button>
          <Button
            type="primary"
            className="grow px-20"
            onClick={() => {
              if (columnConfigRef.current)
                setContextColumnConfig(columnConfigRef.current);
              setIsOpen(false);
            }}
            data-testid="rename-columns-save"
          >
            Save
          </Button>
        </div>
      }
      open={isOpen}
      onCancel={() => setIsOpen(false)}
      destroyOnClose
      data-testid="rename-columns-modal"
    >
      <div className="space-y-4 w-full text-xs">
        <div
          className="flex text-xs space-x-2 items-center"
          data-testid="rename-columns-info-banner"
        >
          <div className="border border-solid border-ciq-blue-300 rounded-lg p-1 font-bold text-xs bg-ciq-blue-100">
            .CSV
          </div>
          <div className="text-black text-opacity-80 text-xs">
            The renamed columns will be visible within this report and its
            export.
          </div>
        </div>

        <Divider />
        <div className="text-ciq-gray-700 text-opacity-80 font-medium text-xs">
          Columns
        </div>

        <div
          className="flex flex-col space-y-8 pl-2"
          data-testid="rename-columns-list"
        >
          {columnConfigRef.current?.map((col: ContextColumnConfigType) => (
            <RenameColumnEditorAndRenderer
              key={col.field_key}
              col={col}
              level={0}
              onChange={handleChange}
            />
          ))}
        </div>
      </div>
    </Modal>
  );
}

export default RenameColumnsModal;
